import { Checkbox, Radio, Segmented, Tooltip } from 'antd';
import ColumnVisibilityControl from 'components/reusable/ColumnVisibilityControl';
import {
  ANALYSIS_COLUMN_LABELS,
  ANALYSIS_GROUP_BY_CROP_VARIETY,
  ANALYSIS_GROUP_BY_FARM,
  ANALYSIS_GROUP_BY_NONE,
  ANALYSIS_PRICE_EXPECTED,
  ANALYSIS_PRICE_OPTIMISTIC,
  ANALYSIS_PRICE_PESSIMISTIC,
  ANALYSIS_PRODUCTION_EXPECTED,
  ANALYSIS_PRODUCTION_LOW,
  ANALYSIS_PRODUCTION_POTENTIAL,
  ANALYSIS_SUMMARY_AVERAGE,
  ANALYSIS_SUMMARY_TOTAL,
  SUMMARY_ENTITY_LABELS,
  FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL,
  FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useCallback, useMemo, useState } from 'react';
import { LuCheck, LuChevronDown, LuChevronUp, LuCopy } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/Store';
import SectionCard from 'pages/FarmsPage/Workspace/Insights/summary2/SectionCard';
import {
  AnalysisColumnKey,
  AnalysisGroupBy,
  AnalysisPriceScenario,
  AnalysisProductionScenario,
  AnalysisSummaryMode,
  setAnalysisCategory,
  setAnalysisGroupBy,
  setAnalysisHiddenColumns,
  setAnalysisPriceScenario,
  setAnalysisProductionScenario,
  setAnalysisShowPerHa,
  setAnalysisSummaryMode,
  setAnalysisWeightAverageByHa,
  toggleAnalysisColumn,
} from '../../../../redux/slices/InsightsSlice';
import AnalysisGrid, { aggregateRowSet, type RowAggregates } from './AnalysisGrid';
import {
  ANALYSIS_COLUMN_ORDER,
  ANALYSIS_COST_CATEGORIES,
  computeAnalysis,
  resolveDistinctYieldUnits,
  resolveGroupKey,
  type AnalysisRow,
} from './computeAnalysis';

interface AnalysisViewProps {
  inputs: IInput[];
  farms: IFarm[];
  gUnits: GUnit[];
  scopeName?: string;
}

const fmt2 = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatCurrency = (value: number): string => {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return `${value < 0 ? '-' : ''}$${formatted}`;
};

const formatPercent = (value: number | null): string =>
  value === null ? '' : `${Math.round(value * 100)}%`;

/** Matches AnalysisGrid's date formatting exactly, so the export reads the same as the on-screen column. */
const formatStartDate = (value: string | null): string => {
  if (!value) return '';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMMM DD, YYYY') : '';
};

// Human-readable labels for the collapsed-ribbon summary line.
const PRICE_SCENARIO_LABELS: Record<AnalysisPriceScenario, string> = {
  [ANALYSIS_PRICE_PESSIMISTIC]: 'Pessimistic price',
  [ANALYSIS_PRICE_EXPECTED]: 'Expected price',
  [ANALYSIS_PRICE_OPTIMISTIC]: 'Optimistic price',
};
const PRODUCTION_SCENARIO_LABELS: Record<AnalysisProductionScenario, string> = {
  [ANALYSIS_PRODUCTION_LOW]: 'Low production',
  [ANALYSIS_PRODUCTION_EXPECTED]: 'Expected production',
  [ANALYSIS_PRODUCTION_POTENTIAL]: 'Potential production',
};
// null (not a string) for "none" — omitted from the collapsed-ribbon summary
// entirely rather than saying "not grouped", since that's the default state.
const GROUP_BY_LABELS: Record<AnalysisGroupBy, string | null> = {
  [ANALYSIS_GROUP_BY_NONE]: null,
  [ANALYSIS_GROUP_BY_FARM]: 'Grouped by Farm',
  [ANALYSIS_GROUP_BY_CROP_VARIETY]: 'Grouped by Crop',
};

/** Label for the column picker — plain, no $/ha or unit suffix. */
const getColumnLabel = (key: AnalysisColumnKey): string =>
  ANALYSIS_COLUMN_LABELS[key] ?? SUMMARY_ENTITY_LABELS[key] ?? key;

const ANALYSIS_MONEY_COLUMNS: ReadonlySet<AnalysisColumnKey> =
  new Set<AnalysisColumnKey>([
    'income',
    'totalCosts',
    'grossMargin',
    ...ANALYSIS_COST_CATEGORIES,
  ]);

/** Header label for the copy-to-clipboard export — mirrors AnalysisGrid's on-screen headers exactly. */
const resolveHeaderLabel = (
  key: AnalysisColumnKey,
  moneySuffix: string,
  uniformYieldUnit: string | null
): string => {
  if (key === 'yield') return uniformYieldUnit ? `Yield (${uniformYieldUnit}/ha)` : 'Yield';
  if (key === 'production')
    return uniformYieldUnit ? `Prod. (${uniformYieldUnit})` : 'Prod.';
  if (key === 'areaHa') return 'Area (ha)';
  const baseLabel = getColumnLabel(key);
  return ANALYSIS_MONEY_COLUMNS.has(key) ? `${baseLabel}${moneySuffix}` : baseLabel;
};

/** Row cell value for the copy-to-clipboard export — mirrors AnalysisGrid's on-screen cells exactly. */
const resolveRowValue = (
  key: AnalysisColumnKey,
  row: AnalysisRow,
  showPerHa: boolean,
  yieldAmbiguous: boolean
): string => {
  switch (key) {
    case 'farmName':
      return row.farmName;
    case 'areaHa':
      return fmt2(row.areaHa);
    case 'crop':
      return row.crop;
    case 'variety':
      return row.variety;
    case 'startDate':
      return formatStartDate(row.startDate);
    case 'price':
      return formatCurrency(row.price);
    case 'yield':
      return yieldAmbiguous
        ? `${fmt2(row.yield)} ${row.yieldUnit}/ha`
        : fmt2(row.yield);
    case 'production':
      return yieldAmbiguous
        ? `${fmt2(row.production)} ${row.yieldUnit}`
        : fmt2(row.production);
    case 'income':
      return formatCurrency(showPerHa ? row.incomePerHa : row.incomeTotal);
    case 'totalCosts':
      return formatCurrency(
        showPerHa ? row.totalCostsPerHa : row.totalCostsTotal
      );
    case 'grossMargin':
      return formatCurrency(
        showPerHa ? row.grossMarginPerHa : row.grossMarginTotal
      );
    case 'costPercentIncome':
      return formatPercent(row.costPercentIncome);
    default:
      return formatCurrency(
        (showPerHa ? row.costsPerHa : row.costsTotal)[key] ?? 0
      );
  }
};

/**
 * Group subtotal row value for the copy-to-clipboard export — mirrors
 * AnalysisGrid's group header cells exactly. Unlike resolveRowValue, the
 * aggregate's money fields already have Total/Average and $/ha baked in
 * (aggregateRowSet resolved that), so there's no showPerHa branch here.
 * Columns AnalysisGrid leaves blank on a group row (Farm Name, Crop,
 * Variety, Start Date) are blank here too; Price/Yield/Production follow
 * the same "only meaningful in Average mode" / "only with a uniform yield
 * unit" rules as the on-screen Dash placeholders, using '' in their place.
 */
const resolveGroupRowValue = (
  key: AnalysisColumnKey,
  aggregates: RowAggregates,
  isAverage: boolean,
  yieldAmbiguous: boolean
): string => {
  switch (key) {
    case 'farmName':
    case 'crop':
    case 'variety':
    case 'startDate':
      return '';
    case 'areaHa':
      return fmt2(aggregates.area);
    case 'price':
      return isAverage ? formatCurrency(aggregates.meanPrice) : '';
    case 'yield':
      return isAverage && !yieldAmbiguous ? fmt2(aggregates.meanYield) : '';
    case 'production':
      return yieldAmbiguous ? '' : fmt2(aggregates.production);
    case 'income':
      return formatCurrency(aggregates.income);
    case 'totalCosts':
      return formatCurrency(aggregates.totalCosts);
    case 'grossMargin':
      return formatCurrency(aggregates.grossMargin);
    case 'costPercentIncome':
      return formatPercent(aggregates.costPercent);
    default:
      return formatCurrency(aggregates.costsByCategory[key] ?? 0);
  }
};

/** A labelled cluster of related ribbon controls (e.g. "Outlook" = Price + Production), stacked vertically to save horizontal ribbon space. Groups with more rows can opt into a 2-column layout via `columns` to avoid growing too tall. */
const RibbonGroup: React.FC<{
  label: string;
  columns?: 1 | 2;
  children: React.ReactNode;
}> = ({ label, columns = 1, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-bold uppercase tracking-wide text-gray-muted">
      {label}
    </span>
    <div
      className={columns === 2 ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-2'}
    >
      {children}
    </div>
  </div>
);

/** One control row inside a RibbonGroup — label and control share a single white "field" chip (rather than floating side by side on the panel background) so they read as one connected unit. The label optionally carries a tooltip explaining what the control affects. */
const RibbonRow: React.FC<{
  label: string;
  tooltip?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ label, tooltip, className, children }) => {
  const labelSpan = (
    <span
      className={`shrink-0 whitespace-nowrap text-sm font-semibold text-gray-muted ${tooltip ? 'cursor-help underline decoration-dotted decoration-gray-argent underline-offset-2' : ''}`}
    >
      {label}:
    </span>
  );
  return (
    <div
      className={`flex items-center gap-1 rounded-md border border-gray-smoke bg-white px-3 py-1.5 ${className ?? ''}`}
    >
      {tooltip ? <Tooltip title={tooltip}>{labelSpan}</Tooltip> : labelSpan}
      {children}
    </div>
  );
};

const RibbonDivider: React.FC = () => (
  <span className="w-px bg-gray-argent" />
);

const AnalysisView: React.FC<AnalysisViewProps> = ({
  inputs,
  farms,
  gUnits,
  scopeName,
}) => {
  const dispatch = useDispatch();
  const {
    analysisCategory,
    analysisPriceScenario,
    analysisProductionScenario,
    analysisShowPerHa,
    analysisHiddenColumns,
    analysisSummaryMode,
    analysisWeightAverageByHa,
    analysisGroupBy,
  } = useSelector((state: RootState) => state.insightsReducer);

  const [isRibbonExpanded, setIsRibbonExpanded] = useState(true);

  // Shown in place of the full ribbon when collapsed, so the current
  // settings stay visible/informative rather than just disappearing.
  const ribbonSummary = [
    analysisCategory === InputTaskCategory.Plan ? 'Plan' : 'Actual',
    PRICE_SCENARIO_LABELS[analysisPriceScenario],
    PRODUCTION_SCENARIO_LABELS[analysisProductionScenario],
    analysisShowPerHa ? '$/ha' : 'Total $',
    analysisSummaryMode === ANALYSIS_SUMMARY_TOTAL ? 'Total' : 'Average',
    GROUP_BY_LABELS[analysisGroupBy],
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const rows = useMemo(
    () =>
      computeAnalysis(
        inputs,
        farms,
        analysisCategory,
        analysisPriceScenario,
        analysisProductionScenario,
        gUnits
      ),
    [
      inputs,
      farms,
      analysisCategory,
      analysisPriceScenario,
      analysisProductionScenario,
      gUnits,
    ]
  );

  // Keyed by group label (farm name, or crop — variety), not by grouping
  // mode — deliberately NOT reset when analysisGroupBy changes, so a user's
  // collapse choices are remembered if they switch group-by and switch back.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const handleToggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groupKeys = useMemo(() => {
    if (analysisGroupBy === ANALYSIS_GROUP_BY_NONE) return [];
    return Array.from(
      new Set(rows.map((row) => resolveGroupKey(row, analysisGroupBy)))
    );
  }, [rows, analysisGroupBy]);

  const allGroupsCollapsed =
    groupKeys.length > 0 && groupKeys.every((k) => collapsedGroups.has(k));
  // Add/remove only this mode's keys — replacing the whole set would wipe
  // out whatever was collapsed under the other Group By mode.
  const handleExpandAll = () =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((k) => next.delete(k));
      return next;
    });
  const handleCollapseAll = () =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((k) => next.add(k));
      return next;
    });

  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    const moneySuffix = analysisShowPerHa ? ' ($/ha)' : ' ($)';
    const distinctYieldUnits = resolveDistinctYieldUnits(rows);
    const uniformYieldUnit =
      distinctYieldUnits.length === 1 ? distinctYieldUnits[0] : null;
    const yieldAmbiguous = !uniformYieldUnit;
    const hidden = new Set(analysisHiddenColumns);
    const visibleColumns = ANALYSIS_COLUMN_ORDER.filter(
      (key) => !hidden.has(key)
    );

    const headerCols = [
      'Paddock',
      ...visibleColumns.map((key) =>
        resolveHeaderLabel(key, moneySuffix, uniformYieldUnit)
      ),
    ];

    const csvRows: string[] = [];
    if (scopeName) csvRows.push(scopeName);
    csvRows.push(headerCols.join('\t'));

    const writeRow = (row: AnalysisRow) => {
      const cols = [
        row.paddockName,
        ...visibleColumns.map((key) =>
          resolveRowValue(key, row, analysisShowPerHa, yieldAmbiguous)
        ),
      ];
      csvRows.push(cols.join('\t'));
    };

    if (analysisGroupBy === ANALYSIS_GROUP_BY_NONE) {
      rows.forEach(writeRow);
    } else {
      // Always includes every group's subtotal, regardless of its on-screen
      // collapsed/expanded state — collapsing is a display-only convenience,
      // and the export should never silently drop data because of it.
      const isAverage = analysisSummaryMode === ANALYSIS_SUMMARY_AVERAGE;
      const groups = new Map<string, AnalysisRow[]>();
      for (const row of rows) {
        const key = resolveGroupKey(row, analysisGroupBy);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(row);
      }
      const sortedKeys = Array.from(groups.keys()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
      for (const key of sortedKeys) {
        const groupRows = groups.get(key)!;
        const aggregates = aggregateRowSet(
          groupRows,
          analysisShowPerHa,
          analysisSummaryMode,
          analysisWeightAverageByHa
        );
        const groupCols = [
          key,
          ...visibleColumns.map((col) =>
            resolveGroupRowValue(col, aggregates, isAverage, yieldAmbiguous)
          ),
        ];
        csvRows.push(groupCols.join('\t'));
        groupRows.forEach(writeRow);
      }
    }

    navigator.clipboard.writeText(csvRows.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [
    rows,
    analysisShowPerHa,
    analysisHiddenColumns,
    scopeName,
    analysisGroupBy,
    analysisSummaryMode,
    analysisWeightAverageByHa,
  ]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden">
      {/* Same "config" card as Farm diagnostics' Yearly Overview, sized to
          its controls rather than stretching across the grid below. */}
      <div className="w-fit max-w-full">
        <SectionCard
          id="analysis-settings"
          title="Analysis settings"
          variant="config"
          collapsed={!isRibbonExpanded}
          actions={
            <button
              type="button"
              className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
              onClick={() => setIsRibbonExpanded((v) => !v)}
            >
              {isRibbonExpanded ? (
                <LuChevronUp size={14} />
              ) : (
                <LuChevronDown size={14} />
              )}
              {isRibbonExpanded
                ? FARM_DIAGNOSTICS_SECTION_COLLAPSE_LABEL
                : FARM_DIAGNOSTICS_SECTION_EXPAND_LABEL}
            </button>
          }
        >
          {isRibbonExpanded ? (
            <div className="flex flex-row flex-wrap gap-6">
              <RibbonGroup label="Outlook">
                <RibbonRow
                  label="Price"
                  tooltip="Which price scenario to use when calculating Income and Gross Margin."
                >
                  <Radio.Group
                    value={analysisPriceScenario}
                    onChange={(e) =>
                      dispatch(
                        setAnalysisPriceScenario(
                          e.target.value as AnalysisPriceScenario
                        )
                      )
                    }
                  >
                    <Radio value={ANALYSIS_PRICE_PESSIMISTIC}>Pessimistic</Radio>
                    <Radio value={ANALYSIS_PRICE_EXPECTED}>Expected</Radio>
                    <Radio value={ANALYSIS_PRICE_OPTIMISTIC}>Optimistic</Radio>
                  </Radio.Group>
                </RibbonRow>
                <RibbonRow
                  label="Production"
                  tooltip="Which production (yield) scenario to use when calculating Income and Gross Margin."
                >
                  <Radio.Group
                    value={analysisProductionScenario}
                    onChange={(e) =>
                      dispatch(
                        setAnalysisProductionScenario(
                          e.target.value as AnalysisProductionScenario
                        )
                      )
                    }
                  >
                    <Radio value={ANALYSIS_PRODUCTION_LOW}>Low</Radio>
                    <Radio value={ANALYSIS_PRODUCTION_EXPECTED}>Expected</Radio>
                    <Radio value={ANALYSIS_PRODUCTION_POTENTIAL}>Potential</Radio>
                  </Radio.Group>
                </RibbonRow>
              </RibbonGroup>

              <RibbonDivider />

              <RibbonGroup label="View options" columns={2}>
                <RibbonRow
                  label="Status"
                  tooltip="Show planned figures, or what was actually recorded."
                >
                  <Radio.Group
                    value={analysisCategory}
                    onChange={(e) =>
                      dispatch(
                        setAnalysisCategory(e.target.value as InputTaskCategory)
                      )
                    }
                  >
                    <Radio value={InputTaskCategory.Plan}>Plan</Radio>
                    <Radio value={InputTaskCategory.Actual}>Actual</Radio>
                  </Radio.Group>
                </RibbonRow>
                <RibbonRow
                  label="Group by"
                  tooltip="Group paddock rows by Farm or by Crop/Variety, with a collapsible subtotal for each group."
                >
                  <Segmented<AnalysisGroupBy>
                    value={analysisGroupBy}
                    onChange={(value) => dispatch(setAnalysisGroupBy(value))}
                    options={[
                      { label: 'None', value: ANALYSIS_GROUP_BY_NONE },
                      { label: 'Farm', value: ANALYSIS_GROUP_BY_FARM },
                      { label: 'Crop', value: ANALYSIS_GROUP_BY_CROP_VARIETY },
                    ]}
                  />
                </RibbonRow>
                <RibbonRow
                  label="Show"
                  tooltip="Display money columns as a per-hectare rate or as a total dollar figure."
                >
                  <Segmented
                    value={analysisShowPerHa ? 'perHa' : 'total'}
                    onChange={(value) =>
                      dispatch(setAnalysisShowPerHa(value === 'perHa'))
                    }
                    options={[
                      { label: '$/ha', value: 'perHa' },
                      { label: 'Total $', value: 'total' },
                    ]}
                  />
                </RibbonRow>
                <RibbonRow
                  label="Summary"
                  tooltip="Total sums every visible paddock's figures; Average shows the mean across them."
                  className="col-span-2"
                >
                  <Segmented<AnalysisSummaryMode>
                    value={analysisSummaryMode}
                    onChange={(value) => dispatch(setAnalysisSummaryMode(value))}
                    options={[
                      { label: 'Total', value: ANALYSIS_SUMMARY_TOTAL },
                      { label: 'Average', value: ANALYSIS_SUMMARY_AVERAGE },
                    ]}
                  />
                  <Checkbox
                    checked={analysisWeightAverageByHa}
                    disabled={
                      analysisSummaryMode !== ANALYSIS_SUMMARY_AVERAGE ||
                      !analysisShowPerHa
                    }
                    onChange={(e) =>
                      dispatch(setAnalysisWeightAverageByHa(e.target.checked))
                    }
                  >
                    <Tooltip title="Weight the average by each paddock's area, rather than treating every paddock equally regardless of size.">
                      <span className="cursor-help underline decoration-dotted decoration-gray-argent underline-offset-2">
                        Weight avg by ha
                      </span>
                    </Tooltip>
                  </Checkbox>
                </RibbonRow>
              </RibbonGroup>
            </div>
          ) : (
            <span className="text-sm text-gray-dark">{ribbonSummary}</span>
          )}
        </SectionCard>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm font-bold text-charcoal">
        {scopeName && <span>{scopeName}</span>}
        {rows.length > 0 && (
          <div className="ml-auto flex items-center gap-3">
            {analysisGroupBy !== ANALYSIS_GROUP_BY_NONE && (
              <>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
                  onClick={allGroupsCollapsed ? handleExpandAll : handleCollapseAll}
                >
                  {allGroupsCollapsed ? (
                    <LuChevronDown size={14} />
                  ) : (
                    <LuChevronUp size={14} />
                  )}
                  {allGroupsCollapsed ? 'Expand all' : 'Collapse all'}
                </button>
                <span className="h-4 border-l border-gray-argent" />
              </>
            )}
            <ColumnVisibilityControl<AnalysisColumnKey>
              columns={ANALYSIS_COLUMN_ORDER}
              getLabel={getColumnLabel}
              groups={[{ label: 'Costs', keys: ANALYSIS_COST_CATEGORIES }]}
              hidden={analysisHiddenColumns}
              onToggle={(key) => dispatch(toggleAnalysisColumn(key))}
              onSetHidden={(cols) => dispatch(setAnalysisHiddenColumns(cols))}
              onShowAll={() => dispatch(setAnalysisHiddenColumns([]))}
            />
            <span className="h-4 border-l border-gray-argent" />
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover"
              onClick={handleCopy}
              title="Copy to clipboard"
            >
              {copied ? <LuCheck size={14} /> : <LuCopy size={14} />}
              {copied ? 'Copied' : 'Copy to clipboard'}
            </button>
          </div>
        )}
      </div>

      <AnalysisGrid
        rows={rows}
        showPerHa={analysisShowPerHa}
        hiddenColumns={analysisHiddenColumns}
        summaryMode={analysisSummaryMode}
        weightAverageByHa={analysisWeightAverageByHa}
        groupBy={analysisGroupBy}
        collapsedGroups={collapsedGroups}
        onToggleGroup={handleToggleGroup}
      />
    </div>
  );
};

export default AnalysisView;
